import { useParams } from "react-router"
import { useState, useEffect, useCallback } from "react"
import BirddogLayout from "../components/BirddogLayout"
import { LumpSumBid, PayAsCutBid, Sale, Bid } from "../types"
import { fetchSale, fetchSaleBids } from "../services/salesApi"

export const BidList = () => {
  const { saleId } = useParams<{ saleId: string }>()
  const [sale, setSale] = useState<Sale | null>(null)
  const [bids, setBids] = useState<Bid[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>("")

  const loadSaleAndBids = useCallback(async () => {
    if (!saleId) return

    try {
      setLoading(true)
      setError("")

      const [saleData, bidsData] = await Promise.all([
        fetchSale(Number(saleId)),
        fetchSaleBids(Number(saleId)),
      ])

      setSale(saleData)
      setBids(
        bidsData.sort(
          (a, b) =>
            new Date(b.submitted_at).getTime() -
            new Date(a.submitted_at).getTime()
        )
      )
    } catch (err) {
      console.error("Failed to fetch sale and bids:", err)
      setError(err instanceof Error ? err.message : "Failed to load data")
    } finally {
      setLoading(false)
    }
  }, [saleId])

  useEffect(() => {
    loadSaleAndBids()
  }, [loadSaleAndBids])

  const handleRetry = () => {
    loadSaleAndBids()
  }

  // Loading state
  if (loading) {
    return (
      <BirddogLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex items-center space-x-3">
            <svg
              className="animate-spin h-6 w-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-gray-600">Loading bids...</span>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  // Error state
  if (error) {
    return (
      <BirddogLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center">
              <svg
                className="h-6 w-6 text-red-400 mr-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Failed to load bids
                </h3>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
            <div className="mt-4">
              <button
                onClick={handleRetry}
                className="bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  // Sale not found state
  if (!sale) {
    return (
      <BirddogLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Sale not found</h1>
            <p className="text-gray-600 mt-2">
              The requested sale could not be found.
            </p>
          </div>
        </div>
      </BirddogLayout>
    )
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const renderLumpSumBid = (bid: LumpSumBid) => (
    <div key={bid.id} className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {bid.bidder_name}
          </h3>
          <p className="text-sm text-gray-500">
            Submitted {bid.submitted_at.toLocaleDateString()}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(bid.total_amount)}
          </p>
          <p className="text-sm text-gray-500 capitalize">{bid.type}</p>
        </div>
      </div>
    </div>
  )

  const renderPayAsCutBid = (bid: PayAsCutBid) => {
    // Calculate estimated totals for each product
    const productTotals = bid.product_prices.map((priceData) => {
      const saleProduct = sale.timber_products.find(
        (p) => p.name === priceData.product_name
      )
      const estimatedVolume = saleProduct?.estimated_volume || 0
      const estimatedTotal = priceData.price_per_unit * estimatedVolume

      return {
        ...priceData,
        estimatedVolume,
        estimatedTotal,
      }
    })

    // Calculate grand total
    const grandTotal = productTotals.reduce(
      (sum, product) => sum + product.estimatedTotal,
      0
    )

    return (
      <div key={bid.id} className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {bid.bidder_name}
            </h3>
            <p className="text-sm text-gray-500">
              Submitted {bid.submitted_at.toLocaleDateString()}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(grandTotal)}
            </p>
            <p className="text-sm text-gray-500 capitalize">{bid.type}</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table-auto divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product Type
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bid Price
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estimated Volume
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estimated Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {productTotals.map((product, index) => (
                <tr key={index}>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    {product.product_name}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(product.price_per_unit)}/ton
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                    {product.estimatedVolume.toLocaleString()} ton
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                    {formatCurrency(product.estimatedTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr className="font-bold">
                <td
                  colSpan={3}
                  className="px-3 py-2 text-sm text-gray-400 text-right"
                >
                  Estimated Total:
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-400 text-right">
                  {formatCurrency(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    )
  }

  return (
    <BirddogLayout>
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Bids</h1>
        <p className="text-gray-600 mt-2">
          {bids.length} {bids.length === 1 ? "bid" : "bids"} for: {sale.name}
        </p>

        <div className="mt-8">
          {bids.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <p className="text-gray-500">
                No bids have been submitted for this sale yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {bids.map((bid) => {
                if (bid.type === "lump sum") {
                  return renderLumpSumBid(bid)
                } else {
                  return renderPayAsCutBid(bid)
                }
              })}
            </div>
          )}
        </div>
      </div>
    </BirddogLayout>
  )
}
